import React from "react";
import { Typography, Button, Box } from "@mui/material";
import { Link } from "react-router-dom";
import DOMPurify from "dompurify"; 

const SlideContent = ({ 
    item, 
    isMobile = false, 
    isCentered = false, 
    align = "left",
    descriptionRef = null,
    isOverflowing = false,
    onReadMore = null
}) => {
    // Custom media query para sa "maliit na desktop"
    const SMALL_DESKTOP_RANGE = "@media (min-width: 676px) and (max-width: 1000px)";

    let vis = {
        headline: { desktop: true, mobile: true },
        subheadline: { desktop: true, mobile: true },
        body: { desktop: true, mobile: true },
        button: { desktop: true, mobile: true },
    };

    if (item.visibility && typeof item.visibility === 'object') {
        vis = item.visibility;
    } else if (typeof item.visibility === 'string' && item.visibility.startsWith('{')) {
        try { vis = JSON.parse(item.visibility); } catch (e) {}
    }

    const shouldShow = (element) => isMobile ? vis[element]?.mobile !== false : vis[element]?.desktop !== false;

    let buttonJustify = "flex-start";
    if (isCentered || align === "center") buttonJustify = "center";
    else if (align === "right") buttonJustify = "flex-end";

    const isExternalLink = item.link_to ? item.link_to.startsWith('http') : false;

    return (
        <>
            {/* HEADLINE / TITLE */}
            {item.title && shouldShow('headline') && (
                <Typography 
                    variant="h2" 
                    sx={{ 
                        fontWeight: isMobile ? 800 : (isCentered ? 800 : 790), 
                        lineHeight: 1.1, 
                        letterSpacing: 1,
                        fontSize: isMobile 
                            ? "clamp(1.8rem, 6svh, 3.5rem)" 
                            : (isCentered ? { md: "4.5rem", lg: "6rem", xl: "7rem" } : { md: "3rem", lg: "4rem" }),
                        
                        [SMALL_DESKTOP_RANGE]: {
                            // fontSize: isCentered ? "3.5rem" : "2.2rem",
                            fontSize: "2.5rem",
                            whiteSpace: "normal",
                        },
                        
                        whiteSpace: isCentered && !isMobile ? "nowrap" : "normal",
                        mb: 0,
                        wordBreak: "break-word"
                    }}
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item.title) }}
                />
            )}

            {/* SUBTITLE */}
            {item.subtitle && shouldShow('subheadline') && (
                <Typography 
                    sx={{ 
                        mt: isMobile ? "clamp(0.2rem, 1svh, 1rem)" : (isCentered ? 0 : { xl: 5, lg: 3, md: 2 }), 
                        fontSize: isMobile ? "clamp(1.3rem, 4svh, 2.7rem)" : "27px", 
                        // CHANGE: Breakpoint added directly in return for 344px-360px
                        "@media (min-width: 344px) and (max-width: 360px)": {
                            fontSize: "2.5rem", 
                            mt: 0.5
                        },

                        [SMALL_DESKTOP_RANGE]: {
                            fontSize: "2rem",
                            mt: 1,
                            lineHeight: 1.2
                        },

                        color: "#444",
                        fontWeight: isMobile ? 500 : (isCentered ? 700 : 400),
                        mb: 0
                    }}
                >
                    {item.subtitle}
                </Typography>
            )}

            {/* DESCRIPTION / BODY */}
            {item.description && shouldShow('body') && (
                <Typography
                    component="div"
                    ref={descriptionRef}
                    sx={{
                        py: 0, 
                        mt: isMobile ? "clamp(0.5rem, 1.5svh, 1rem)" : { xs: 0, md: 2, lg: 3 },
                        mb: isCentered ? 4 : 0,
                        color: "#666",
                        maxWidth: isCentered ? "100%" : "85%",
                        '& p, & li': { 
                            margin: 0, 
                            padding: 0, 
                            mb: isMobile ? "clamp(0.5rem, 1.5svh, 1.5rem)" : 1.5,
                            fontSize: isMobile ? "clamp(1.1rem, 3.2svh, 2.4rem)" : "19px", 
                            lineHeight: 1.25, 
                            fontWeight: 400, 
                            fontFamily: 'inherit',

                            // CHANGE: Breakpoint added directly in return for 344px-360px
                            "@media (min-width: 344px) and (max-width: 360px)": {
                                fontSize: "2.21rem",
                                mb: 0.5,
                                // lineHeight: 4
                            },
                        //      "@media (min-width: 380px) and (max-width: 675px)": {
                        //    lineHeight: 1.25,
                        //    mb: 5
                        // },

                            [SMALL_DESKTOP_RANGE]: {
                                mt: 1,
                                fontSize: "15px",
                                mb: 1,
                            },
                        },
                        '& p:last-of-type': { mb: 0 },
                        '& ul, & ol': { margin: 0, paddingLeft: 3 },
                        '& strong, & b': { fontWeight: 700 },
                        '& em, & i': { fontStyle: 'italic' },
                        '& span': { fontSize: 'inherit', fontFamily: 'inherit' }
                    }}
                    dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(item.description)
                    }}
                />
            )}
            
            {/* BUTTONS CONTAINER */}
            <Box sx={{ 
                display: "flex", 
                flexDirection: isMobile && isCentered ? "column" : "row", 
                justifyContent: buttonJustify, 
                gap: isMobile ? "clamp(0.5rem, 1.5svh, 1rem)" : 2, 
                mt: isMobile ? "clamp(1rem, 2svh, 2rem)" : { xs: 0, md: 3, lg: 4 },
                
                [SMALL_DESKTOP_RANGE]: {
                    mt: 2,
                    gap: 1.5
                }
            }}>
                {/* MAIN ACTION BUTTON */}
                {item.button_text && item.link_to && shouldShow('button') && (
                    <Button
                        component={isExternalLink ? "a" : Link}
                        href={isExternalLink ? item.link_to : undefined}
                        to={!isExternalLink ? item.link_to : undefined}
                        target={isExternalLink ? "_blank" : undefined}
                        rel={isExternalLink ? "noopener noreferrer" : undefined}
                        variant="contained"
                        fullWidth={isMobile && isCentered}
                        sx={{
                            fontSize:  "15px", 
                            fontWeight: 700,
                            letterSpacing: 2,
                            backgroundColor: "#00b5e2",
                            px: isMobile ? "40%" : 10, 
                            // py: isMobile ? "clamp(0.6rem, 1.5svh, 1.5rem)" : (isCentered ? 1.2 : 1.2),
                            // mt: isMobile ? 1 : "auto",
                            whiteSpace: "nowrap",
                            borderRadius: "50px",
                            "&:hover": { backgroundColor: "#009fc7" },
                            [SMALL_DESKTOP_RANGE]: {
                                px: 5,
                                py: 1,
                                fontSize: "12px",
                          
                            }
                        }}
                    >
                        {item.button_text}
                    </Button>
                )}
            </Box>
        </>
    );
};

export default SlideContent;